import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import App from './App'
import { AuthProvider } from './lib/auth/AuthProvider'
import { JurisdictionProvider } from './lib/jurisdiction/JurisdictionProvider'
import { PendingJurisdictionApplier } from './lib/jurisdiction/PendingJurisdictionApplier'
import { ThemeProvider } from './components/providers/ThemeProvider'
import { ColorThemeProvider, applyColorTheme } from './components/providers/ColorThemeProvider'
import { THEME_STORAGE_KEY, DEFAULT_THEME, isThemeName } from './lib/themes'
import { Toaster } from './components/ui/toaster'
import { CommandPaletteProvider } from './components/ui/command-palette'
import { ConsentBanner } from './components/layout/ConsentBanner'

// Syncfusion CSS imports (only packages that have tailwind.css)
import '@syncfusion/ej2-base/styles/tailwind.css'
import '@syncfusion/ej2-buttons/styles/tailwind.css'
import '@syncfusion/ej2-calendars/styles/tailwind.css'
import '@syncfusion/ej2-dropdowns/styles/tailwind.css'
import '@syncfusion/ej2-inputs/styles/tailwind.css'
import '@syncfusion/ej2-navigations/styles/tailwind.css'
import '@syncfusion/ej2-popups/styles/tailwind.css'
import '@syncfusion/ej2-grids/styles/tailwind.css'
import '@syncfusion/ej2-schedule/styles/tailwind.css'

import './index.css'
import './styles/aurora.css'
import './i18n'
import { installSvgPathGuard } from './lib/svg-path-guard'

// Prevent the recurring browser console error emitted when Recharts (or any
// chart) builds a malformed SVG <path d="...">  from empty / NaN / degenerate
// data — e.g.  <path> attribute d: Expected moveto path command ('M' or 'm'),
// "Z".  This error comes from the native SVG parser, so it cannot be silenced by
// overriding console.error (W24 attempted that and it never worked). The guard
// below normalises any non-moveto `d` value to "" *before* it reaches the DOM,
// so an empty chart simply renders nothing instead of throwing a parser error.
// See src/lib/svg-path-guard.ts for the full rationale.
installSvgPathGuard()

// Defensive: also drop the matching console.error-routed NaN attribute warnings
// (these DO go through console.error and are harmless transient empty-data renders).
{
  const originalError = console.error.bind(console)
  const RECHARTS_SVG_WARNING_PATTERNS = [
    /<path>\s+attribute d:\s+Expected moveto path command/i,
    /<circle>\s+attribute c[xy]:\s+Expected length, "NaN"/i,
    /<polygon>\s+attribute points:\s+Expected number, "NaN/i,
  ]
  console.error = (...args: any[]) => {
    const msg = String(args[0] ?? '')
    if (RECHARTS_SVG_WARNING_PATTERNS.some((re) => re.test(msg))) return
    if (args[0] instanceof Error && RECHARTS_SVG_WARNING_PATTERNS.some((re) => re.test(args[0].message || ''))) return
    originalError(...args)
  }
}

// Apply the persisted color theme BEFORE first paint to avoid a flash of the
// default theme. The full reconcile (backend wins) happens inside ColorThemeProvider.
{
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    applyColorTheme(isThemeName(stored) ? stored : DEFAULT_THEME)
  } catch {
    applyColorTheme(DEFAULT_THEME)
  }
}

// Syncfusion License
import { registerLicense } from '@syncfusion/ej2-base'
registerLicense(import.meta.env.VITE_SYNCFUSION_LICENSE || '')

// React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <JurisdictionProvider>
            <PendingJurisdictionApplier />
            <ThemeProvider defaultTheme="system" storageKey="sgrc-theme">
              <ColorThemeProvider>
                <CommandPaletteProvider>
                  <App />
                  <ConsentBanner />
                </CommandPaletteProvider>
                <Toaster />
              </ColorThemeProvider>
            </ThemeProvider>
          </JurisdictionProvider>
        </AuthProvider>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </React.StrictMode>
)
